/**
 * **端口几何** —— 把 DOM 量出来的矩形换算成"画布坐标系里的端口位置"。
 *
 * ## 这是 `updateNodeInternals` 的替代品
 *
 * 原来这件事由引擎做（`store.updateNodeInternals(ids?)`，全仓库 24 处调用）。
 * 它的职责其实只有一句话：
 *
 * > **节点变了（折行、变参行增删、标题换行、缩放…）→ 重新量一次，
 * > 端口在哪、节点多大，画布得知道** —— 否则连线会接到空气上。
 *
 * 我们自己的实现是三个纯函数 + 一层 DOM 读取（`measureNode`），
 * 因为纯函数可以被直接断言（"ZOOM = 2 时偏移要减半"这种），
 * 而 DOM 那层在 happy-dom 里量不出真尺寸（没有布局）。
 *
 * ## ⚠️ 单位换算：DOM 是**屏幕像素**，画布是**画布单位**
 *
 * 节点层带着 `transform: scale(zoom)`，所以 `getBoundingClientRect()`
 * 量出来的是**被放大之后**的尺寸。折成画布单位要**除以 zoom**：
 *
 * ```text
 *   节点宽度（画布单位） = rect.width / zoom
 *   端口偏移（画布单位） = (端口中心 - 节点左上角) / zoom
 * ```
 *
 * 忘了除 zoom 的表现很隐蔽：zoom = 1 时一切正常，**放大之后连线开始
 * 逐渐偏离端口**（偏差 = 距离 × (zoom-1)），而"缩放之后才歪"这种 bug
 * 很难第一眼归因到单位上。
 *
 * ## ⚠️ 幂等：量出来的值和存着的值"差不多"就不要写
 *
 * 测量 → 写状态 → 触发渲染 → 又测量 …… 这个环一旦闭上是**死循环**
 * （帧率掉到 0，控制台没有报错）。所以比较必须是**带容差的相等**，
 * 见 `sameSocketOffsets` / `sameDimensions`。
 */
import type { Point } from '../../types/geometry'

/** `getBoundingClientRect()` 的子集（happy-dom 里也能造出来，便于测） */
export interface RectLike {
  left: number
  top: number
  width: number
  height: number
}

export interface Size {
  width: number
  height: number
}

/** 矩形中心 */
export function centerOfRect(rect: RectLike): Point {
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
}

/** 节点尺寸（画布单位） */
export function sizeFromRect(rect: RectLike, zoom: number): Size {
  const z = zoom > 0 && Number.isFinite(zoom) ? zoom : 1
  return { width: rect.width / z, height: rect.height / z }
}

/**
 * 端口的中心**相对节点左上角**的偏移（画布单位）。
 *
 * 端口在节点的哪一侧不用管 —— 位置是从 DOM 量的，不在代码里假设。
 */
export function socketOffsetFrom(
  socketRect: RectLike,
  nodeRect: RectLike,
  zoom: number,
): Point {
  const z = zoom > 0 && Number.isFinite(zoom) ? zoom : 1
  const center = centerOfRect(socketRect)
  return {
    x: (center.x - nodeRect.left) / z,
    y: (center.y - nodeRect.top) / z,
  }
}

/** 一批端口（`handleId → 矩形`）的偏移表 */
export function socketOffsetsFrom(
  socketRects: Readonly<Record<string, RectLike>>,
  nodeRect: RectLike,
  zoom: number,
): Record<string, Point> {
  const out: Record<string, Point> = {}
  for (const [id, rect] of Object.entries(socketRects)) {
    out[id] = socketOffsetFrom(rect, nodeRect, zoom)
  }
  return out
}

/* ── 带容差的相等（防测量死循环，见文件头第二条 ⚠️）─────────────
 *
 * 容差 1 画布单位（≈1px）：亚像素抖动不该触发重渲染，
 * 而真实的布局变化（一行 28px）远大于它。
 */
export const MEASURE_EPSILON = 1

function near(a: number, b: number): boolean {
  return Math.abs(a - b) <= MEASURE_EPSILON
}

export function sameSocketOffsets(
  a: Readonly<Record<string, Point>> | undefined,
  b: Readonly<Record<string, Point>> | undefined,
): boolean {
  if (a === b) return true
  const left = a ?? {}
  const right = b ?? {}
  const leftKeys = Object.keys(left)
  const rightKeys = Object.keys(right)
  if (leftKeys.length !== rightKeys.length) return false
  for (const key of leftKeys) {
    const p = left[key]
    const q = right[key]
    if (!p || !q) return false
    if (!near(p.x, q.x) || !near(p.y, q.y)) return false
  }
  return true
}

export function sameDimensions(a: Size | undefined, b: Size | undefined): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return near(a.width, b.width) && near(a.height, b.height)
}

/**
 * 两个端口的**方向**（连线从哪一侧出去）。
 *
 * ⚠️ 不按"输入/输出"猜，而是按**端口在节点里的水平位置**判：
 * 左半边 = 从左侧出，右半边 = 从右侧出。
 * 侧栏折叠、行宽变化都不会让它判错，而"输入一定是左"这种假设
 * 在有锚点（折叠分组的锚点也在左侧）时会给出相反的答案。
 */
export function sideOfSocket(offset: Point, size: Size, ratio = 0.5): 'left' | 'right' {
  if (size.width <= 0) return offset.x <= 0 ? 'left' : 'right'
  return offset.x < size.width * ratio ? 'left' : 'right'
}
